import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, ExternalLink, Filter,
  AlertTriangle, AlertCircle, Info,
} from 'lucide-react';

const SEV_ICON = {
  high: <AlertTriangle size={16} />,
  medium: <AlertCircle size={16} />,
  low: <Info size={16} />,
  info: <Info size={16} />,
};
const SEV_LABEL = { high: 'High', medium: 'Medium', low: 'Low', info: 'Info' };
const SEV_CLASS = { high: 'alert-high', medium: 'alert-medium', low: 'alert-low', info: 'alert-info' };

export default function Alerts({ user, alerts, loading, refresh }) {
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');

  const categories = useMemo(
    () => Array.from(new Set(alerts.map((a) => a.category))).sort(),
    [alerts]
  );

  const filtered = useMemo(
    () => alerts.filter((a) =>
      (filterCategory === 'all' || a.category === filterCategory) &&
      (filterSeverity === 'all' || a.severity === filterSeverity)
    ),
    [alerts, filterCategory, filterSeverity]
  );

  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach((a) => {
      groups[a.category] = groups[a.category] || [];
      groups[a.category].push(a);
    });
    return groups;
  }, [filtered]);

  const highCount = alerts.filter((a) => a.severity === 'high').length;
  const mediumCount = alerts.filter((a) => a.severity === 'medium').length;

  return (
    <div className="page-container">
      <header className="page-header">
        <div className="header-left">
          <Link to="/" className="back-btn" title="Back"><ArrowLeft size={18} /></Link>
          <h1 className="app-title">Alerts</h1>
          {alerts.length > 0 && (
            <div className="alerts-summary-badges">
              {highCount > 0 && <span className="alerts-count-badge alert-high">{highCount} high</span>}
              {mediumCount > 0 && <span className="alerts-count-badge alert-medium">{mediumCount} medium</span>}
              <span className="alerts-count-badge alert-info">{alerts.length} total</span>
            </div>
          )}
        </div>
        <div className="header-right">
          <button className="btn btn-ghost" onClick={() => refresh(true)} title="Refresh">
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
          </button>
        </div>
      </header>

      <main className="alerts-main">
        {/* Filters */}
        <div className="alerts-filters">
          <div className="alerts-filter-group">
            <label><Filter size={12} /> Category</label>
            <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
              <option value="all">All categories</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="alerts-filter-group">
            <label><Filter size={12} /> Severity</label>
            <select value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value)}>
              <option value="all">All</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
              <option value="info">Info</option>
            </select>
          </div>
        </div>

        {loading && !alerts.length ? (
          <div className="alerts-empty">Loading alerts...</div>
        ) : filtered.length === 0 ? (
          <div className="alerts-empty">
            {alerts.length === 0 ? 'All clear — no alerts right now.' : 'No alerts match these filters.'}
          </div>
        ) : (
          Object.entries(grouped).map(([category, items]) => (
            <div key={category} className="alerts-group">
              <div className="alerts-group-header">{category}</div>
              <div className="alerts-group-list">
                {items.map((a) => (
                  <AlertCard key={a.id} alert={a} />
                ))}
              </div>
            </div>
          ))
        )}
      </main>
    </div>
  );
}

function AlertCard({ alert }) {
  const inner = (
    <div className={`alert-card ${SEV_CLASS[alert.severity]}`}>
      <div className="alert-card-icon">{SEV_ICON[alert.severity]}</div>
      <div className="alert-card-body">
        <div className="alert-card-title">{alert.title}</div>
        <div className="alert-card-meta">
          <span className="alert-card-entity">{alert.entity}</span>
          <span className={`alert-card-sev ${SEV_CLASS[alert.severity]}`}>{SEV_LABEL[alert.severity]}</span>
        </div>
      </div>
      <ExternalLink size={14} className="alert-card-go" />
    </div>
  );

  if (alert.isInternal) {
    return <Link to={alert.action} style={{ textDecoration: 'none' }}>{inner}</Link>;
  }
  return <a href={alert.action} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>{inner}</a>;
}
